import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanDeactivate,
  RouterStateSnapshot,
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, from } from 'rxjs';
import Swal from 'sweetalert2';

export interface CanComponentLeave {
  canDeactivate: () => Observable<boolean> | Promise<boolean> | boolean;
  hasUnsavedQuestionnaireChanges?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CanLeaveGuard implements CanDeactivate<CanComponentLeave> {
  constructor(private readonly translate: TranslateService) {}

  canDeactivate(
    component: CanComponentLeave,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot,
  ): Observable<boolean> | Promise<boolean> | boolean {
    if (
      !component.hasUnsavedQuestionnaireChanges &&
      this.isInternalVisitQuestionnaireNavigation(currentState, nextState)
    ) {
      return true;
    }

    if (component.hasUnsavedQuestionnaireChanges) {
      Swal.fire({
        text: `${this.translate.instant(
          'You have unsaved changes in the questionnaire. Please submit to continue.'
        )}`,
        icon: 'warning',
        showConfirmButton: true,
      });
      return false;
    }

    if (component.canDeactivate) {
      return component.canDeactivate();
    }

    return new Promise<boolean>((resolve) => {
      Swal.fire({
        text: `${this.translate.instant(
          'Are you sure you want to leave this visit'
        )}?`,
        showDenyButton: true,
        showCancelButton: true,
        cancelButtonText: `${this.translate.instant('Discard Visit')}`,
        confirmButtonText: `${this.translate.instant('Save as Draft')}`,
      }).then((result) => {
        if (result.isConfirmed) {
          resolve(true); // Allow navigation
        } else if (result.isDenied) {
          resolve(true); // Also allow navigation, but you can handle this differently if needed
        } else {
          resolve(false); // Cancel navigation
        }
      });
    });
  }

  private isInternalVisitQuestionnaireNavigation(
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot,
  ): boolean {
    if (!nextState?.url) {
      return false;
    }

    const currentVisit = this.parseVisitRoute(currentState.url);
    const nextVisit = this.parseVisitRoute(nextState.url);

    if (!currentVisit || !nextVisit) {
      return false;
    }

    return (
      currentVisit.basePath === nextVisit.basePath &&
      currentVisit.patientId === nextVisit.patientId &&
      currentVisit.eocId === nextVisit.eocId &&
      currentVisit.visitId === nextVisit.visitId
    );
  }

  private parseVisitRoute(
    url: string,
  ): {
    basePath: string;
    patientId: string;
    eocId: string;
    visitId: string;
  } | null {
    const match = url.match(
      /^\/ehr\/(episodeOfCare|inPatientView)\/([^/]+)\/([^/]+)\/([^/?#]+)/,
    );

    if (!match) {
      return null;
    }

    return {
      basePath: match[1],
      patientId: match[2],
      eocId: match[3],
      visitId: match[4],
    };
  }
}
